import { useMemo } from 'react'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import { useI18n } from '@popety_io/popety-io-lib'

const AQIGraph = ({
  data,
  dateType,
}: {
  data: Record<string, { average: number }>
  dateType: string
}) => {
  const { t } = useI18n()

  const formatData = useMemo(() => {
    if (dateType === 'hours') {
      const hours = Array.from({ length: 16 }, (_, i) => i + 4)

      return hours.map((hour) => {
        const time = `${hour.toString().padStart(2, '0')}:00`

        return {
          date: time,
          average: data && data[time] ? data[time].average : null,
        }
      })
    }

    return Object.entries(data || {}).map(([date, stats]) => ({
      date,
      average: stats?.average ?? null,
    }))
  }, [data, dateType])

  if (!data || Object.keys(data).length === 0) {
    return <div>{t('common.no_data_available')}</div>
  }

  const colorRanges = [
    { threshold: 20, color: '#93d1ca' },
    { threshold: 40, color: '#86b78a' },
    { threshold: 60, color: '#eaca6f' },
    { threshold: 80, color: '#cd6b5a' },
    { threshold: 100, color: '#9e3e40' },
    { threshold: Infinity, color: '#5b3671' },
  ]

  const { minValue, maxValue } = useMemo(() => {
    const values: any = formatData.map((item) => item.average).filter(Boolean)

    return {
      minValue: Math.floor(Math.min(...values) / 10) * 10,
      maxValue: Math.ceil(Math.max(...values) / 10) * 10,
    }
  }, [formatData])

  return (
    <ResponsiveContainer width="100%" height={150}>
      <LineChart
        data={formatData}
        margin={{ top: 5, right: 30, left: -30, bottom: 5 }}
      >
        <XAxis
          dataKey="date"
          axisLine={{ stroke: '#E5E5E5', strokeWidth: 2 }}
          tickLine={false}
          padding={{ left: 0, right: 0 }}
          tickMargin={10}
          tickFormatter={(tick: string) => {
            if (dateType === 'hours') {
              return tick.slice(0, -3)
            }
            if (dateType === 'days') {
              return tick.split('-')[2]
            }

            return tick
          }}
        />
        <YAxis
          axisLine={{ stroke: '#E5E5E5', strokeWidth: 2 }}
          tickLine={false}
          tick={{ fontSize: 12 }}
          tickMargin={5}
        />
        <defs>
          <linearGradient id="colorGradient" x1="0" y1="1" x2="0" y2="0">
            {colorRanges.map((range, index) => {
              const offset = Math.max(
                0,
                Math.min(
                  1,
                  (range.threshold - minValue) / (maxValue - minValue),
                ),
              )

              return (
                <stop key={index} offset={offset} stopColor={range.color} />
              )
            })}
          </linearGradient>
        </defs>
        <Tooltip />
        <Line
          type="monotone"
          dataKey="average"
          strokeWidth={4}
          stroke="url(#colorGradient)"
          dot={false}
          connectNulls={false}
        />
      </LineChart>
    </ResponsiveContainer>
  )
}

export default AQIGraph
